import humps from 'lodash-humps'
import { action, computed, observable } from 'mobx'
import Activity from './activity'

import {
  ERROR_EXAM_LOAD_ACTIVITY,
  EXAM_ABANDONED,
  EXAM_FINISHED,
  EXAM_READY,
  EXAM_STARTED,
  EXAM_TIMEOUT,
} from '@/constants'
import SentryReplay from '@/SentryReplay'
import { toCamelCase } from '@/utils/helpers'
import request from '@/utils/request'

const EXAM_MODE_PRACTICE = 'practice'

export const EXAM_TYPE_ORAL = 'oral-exam'
export const EXAM_TYPE_DEFAULT = 'exam'

export const ABANDON_EXAM_STATUS_ABANDONED = 'abandoned'
export const ABANDON_EXAM_STATUS_TIMEOUT = 'timeout'

export const EXAM_ANSWER_TYPE_TIMEOUT = 'timeout'

export default class Exam {
  @observable activity = null
  @observable activityAudioSrc = null
  @observable activityError = null
  @observable attempt = 0
  @observable courseId = null
  @observable courseTitle = ''
  @observable defaultQuestionCount = null
  @observable filters = []
  @observable filtersLearningObjectives = null
  @observable filtersNumQuestions = null
  @observable isAbandoningExam = false
  @observable isDownloadingCertificate = false
  @observable examTimeEnabled = false
  @observable isLoadingActivity = false
  @observable isSavingActivity = false
  @observable isSavingTimeRemaining = false
  @observable metadata = null
  @observable mode = null
  @observable nasbaCpeCredits = null
  @observable nextActivity = null
  @observable numActivities = null
  @observable numExamActivitiesRemaining = null
  @observable numExamActivitiesTotal = null
  @observable numSectionsTestedOut = null

  @observable introCopy = null
  @observable outroCopy = null
  // exam passed
  @observable examPassCount = null // # of passed attempts required
  @observable examPassed = false // exam has been passed
  @observable numExamPassed = 0 // # of passed attempts
  // attempt passed
  @observable passed = null // exam attempt is passed
  @observable results = null
  @observable score = null
  @observable status = null
  @observable surveyUrl = null
  @observable timeLimit = null
  @observable timeRemaining = null
  @observable type = EXAM_TYPE_DEFAULT
  @observable validationResponse = null

  // practice exam feedback
  @observable isShowingFeedback = false
  @observable bufferedNextActivity = null
  @observable bufferedActivityAudioSrc = null
  @observable bufferedNumExamActivitiesRemaining = null

  constructor(data) {
    this.init(data)
  }

  init(data = {}) {
    toCamelCase(this, data)
  }

  createNewActivity(activityData) {
    const activity = new Activity({ ...activityData })
    return activity
  }

  @action resetFilters() {
    this.filtersLearningObjectives = null
    this.filtersNumQuestions = null
  }

  @computed get isDone() {
    return (
      this.status === EXAM_ABANDONED ||
      this.status === EXAM_FINISHED ||
      this.status === EXAM_TIMEOUT
    )
  }

  @computed get isIntro() {
    return !this.status || this.isReady
  }

  @computed get isPassed() {
    /*
      isPassed
        - defines whether or not a single Exam Attempt is passed
    */
    return !!this.passed
  }

  @computed get isPassedExam() {
    /*
      isPassedExam
        - defines whether or not Exam as a whole is passed
        - if numExamPassed >= examPassCount should be true
        - returned by API so no need to calculate on UI side
    */
    return !!this.examPassed
  }

  @computed get isReady() {
    return this.status === EXAM_READY
  }

  @computed get isStarted() {
    return this.status === EXAM_STARTED
  }

  @computed get isOral() {
    return this.type === EXAM_TYPE_ORAL
  }

  @computed get isPractice() {
    return this.mode === EXAM_MODE_PRACTICE
  }

  @computed get hasNextActivity() {
    return !!this.bufferedNextActivity
  }

  @action startExam() {
    this.status = EXAM_STARTED
  }

  @action incrementAttempt() {
    this.attempt = this.attempt + 1
    return this.attempt
  }

  @action resetActivity() {
    this.activity = null
    this.activityAudioSrc = null
  }
  @action resetExam() {
    this.timeRemaining = this.timeLimit
    this.resetActivity()
  }

  @action loadActivity = () => {
    this.isLoadingActivity = true
    this.activityError = null
    const examType = this.isPractice
      ? this.isOral
        ? 'practice-oral-exam'
        : 'practice-exam'
      : 'exam'
    const req = this.isPractice
      ? request.post(`/activities/${this.courseId}/${examType}/activity`, {
          learning_objectives: this.filtersLearningObjectives || [],
          question_count: this.filtersNumQuestions || this.defaultQuestionCount,
        })
      : request.get(`/activities/${this.courseId}/${examType}/activity`, {
          params: {
            d: Date.now(),
          },
        })
    return req.then(this.handleLoadActivity).catch(this.handleLoadActivityError)
  }
  @action handleLoadActivity = (response) => {
    const { data } = response
    const {
      activity: activityData,
      activityAudioSrc,
      examPassCount,
      examPassed,
      numExamActivitiesRemaining,
      numExamActivitiesTotal,
      numExamPassed,
      outro: examOutro,
      passed: examAttemptPassed,
      score: examScore,
      status: examStatus,
      timeRemaining: examTimeRemaining,
    } = humps(data)

    this.examPassCount = examPassCount
    this.examPassed = examPassed
    this.numExamActivitiesRemaining = numExamActivitiesRemaining
    this.numExamActivitiesTotal = numExamActivitiesTotal
    this.numExamPassed = numExamPassed
    this.outroCopy = examOutro
    this.passed = examAttemptPassed
    this.score = examScore
    this.status = examStatus
    this.timeRemaining = examTimeRemaining

    if (activityData) {
      this.resetActivity()
      this.activity = this.createNewActivity(activityData)
      if (this.isOral) {
        this.activityAudioSrc = activityAudioSrc || null
      }
    } else if (this.isStarted) {
      return this.handleLoadActivityError(new Error(ERROR_EXAM_LOAD_ACTIVITY))
    }
    this.isLoadingActivity = false
    return Promise.resolve()
  }
  @action handleLoadActivityError = (error) => {
    const { message } = error
    if (message === ERROR_EXAM_LOAD_ACTIVITY) {
      this.activityError = ERROR_EXAM_LOAD_ACTIVITY
    }
    SentryReplay.captureException(error)
    this.isLoadingActivity = false
    return Promise.reject(error)
  }

  @action saveActivity(activityAnswer, answerType = null) {
    if (this.isSavingActivity)
      return Promise.reject(new Error('Already saving activity;'))
    if (!this.isOral && !activityAnswer)
      return Promise.reject(new Error('No answer;'))

    const { id: currentActivityId, isMultipleInput } = this.activity

    this.isSavingActivity = true

    let POST = { current_activity_id: currentActivityId }
    if (this.isOral) {
      POST = { ...POST, answer: activityAnswer || '' }
      if (answerType) {
        POST = { ...POST, answer_type: answerType }
      }
    } else if (isMultipleInput) {
      POST = { ...POST, answers: this.activity.adaptAnswer(activityAnswer) }
    } else {
      POST = { ...POST, answer: this.activity.adaptAnswer(activityAnswer) }
    }

    const examType = this.isOral
      ? 'practice-oral-exam'
      : this.isPractice
      ? 'practice-exam'
      : 'exam'

    return request
      .post(`/activities/${this.courseId}/${examType}/answer`, POST)
      .then(this.handleSaveActivity)
      .catch(this.handleSaveActivityError)
  }
  @action handleSaveActivity = (response) => {
    const { data: responseData } = response
    const data = humps(responseData)
    const {
      activity: nextActivity,
      activityAudioSrc: nextActivityAudioSrc,
      examPassCount,
      examPassed,
      numExamActivitiesRemaining,
      numExamActivitiesTotal,
      numExamPassed,
      outro: examOutro,
      passed: examAttemptPassed,
      response: answerResponse,
      results: examResults,
      score: examScore,
      status: examStatus,
    } = data

    if (this.isOral && this.isPractice) {
      this.bufferedNextActivity = nextActivity
      this.bufferedActivityAudioSrc = nextActivityAudioSrc
      this.bufferedNumExamActivitiesRemaining = numExamActivitiesRemaining
      this.validationResponse = answerResponse
      this.isShowingFeedback = true
    } else if (this.isPractice && answerResponse) {
      const activity = this.activity

      // Set feedback data on current activity
      activity.answerResponse = answerResponse.answer
      activity.correctAnswers = answerResponse.correctAnswers
      activity.incorrectAnswers = answerResponse.incorrectAnswers
      activity.hint = answerResponse.hint
      activity.passed = answerResponse.passed
      activity.isNewActivity = false

      // Buffer next activity and remaining count for when feedback is dismissed
      this.bufferedNextActivity = nextActivity
      this.bufferedNumExamActivitiesRemaining = numExamActivitiesRemaining
      this.isShowingFeedback = true
    } else {
      this.resetActivity()
      if (nextActivity) {
        this.activity = this.createNewActivity(nextActivity)
        if (this.isOral) {
          this.activityAudioSrc = nextActivityAudioSrc || null
        }
      }
      this.numExamActivitiesRemaining = numExamActivitiesRemaining
    }

    // Apply exam-level state
    this.examPassCount = examPassCount
    this.examPassed = examPassed
    this.numExamActivitiesTotal = numExamActivitiesTotal
    this.numExamPassed = numExamPassed
    this.outroCopy = examOutro
    this.passed = examAttemptPassed
    this.results = examResults
    this.score = examScore
    this.status = examStatus

    this.isSavingActivity = false
    return Promise.resolve(response)
  }
  @action advanceFromFeedback = () => {
    this.resetActivity()
    if (this.bufferedNextActivity) {
      this.activity = this.createNewActivity(this.bufferedNextActivity)
      if (this.isOral) {
        this.activityAudioSrc = this.bufferedActivityAudioSrc || null
      }
    }

    this.numExamActivitiesRemaining = this.bufferedNumExamActivitiesRemaining
    this.isShowingFeedback = false
    this.validationResponse = null
    this.bufferedNextActivity = null
    this.bufferedActivityAudioSrc = null
    this.bufferedNumExamActivitiesRemaining = null
  }
  @action handleSaveActivityError = (error) => {
    const { response } = error || {}
    const { status } = response || {}
    SentryReplay.captureException(error)
    this.isSavingActivity = false
    if (status === 403 || status === 409) {
      return this.loadActivity()
    }
    return Promise.reject(error)
  }

  @action abandonExam(status) {
    if (this.isAbandoningExam) return

    this.isAbandoningExam = true

    const examType = this.isOral
      ? 'practice-oral-exam'
      : this.isPractice
      ? 'practice-exam'
      : 'exam'

    return request
      .post(`/activities/${this.courseId}/${examType}/abandon`, { status })
      .then(this.handleAbandonExam)
      .catch(this.handleAbandonExamError)
  }
  @action handleAbandonExam = (response) => {
    const { data: responseData } = response
    const {
      examPassCount,
      examPassed,
      numExamPassed,
      outro: examOutro,
      passed: examAttemptPassed,
      results: examResults,
      score: examScore,
      status: examStatus,
    } = humps(responseData)

    // Transition to done state
    this.examPassCount = examPassCount
    this.examPassed = examPassed
    this.numExamActivitiesRemaining = null
    this.numExamActivitiesTotal = null
    this.numExamPassed = numExamPassed
    this.outroCopy = examOutro
    this.passed = examAttemptPassed
    this.results = examResults
    this.score = examScore
    this.status = examStatus

    this.isAbandoningExam = false
    return Promise.resolve(response)
  }
  @action handleAbandonExamError = (error) => {
    SentryReplay.captureException(error)
    this.isAbandoningExam = false
    return Promise.reject(error)
  }

  validateAnswer(activityAnswer, openIncompleteQuestionModal) {
    return this.activity.validateAnswer(
      activityAnswer,
      openIncompleteQuestionModal,
    )
  }

  @action setTimeRemaining(seconds) {
    if (!this.examTimeEnabled || this.isSavingTimeRemaining) {
      return Promise.resolve()
    }

    this.isSavingTimeRemaining = true
    return request
      .post(
        `/activities/${this.courseId}/${
          this.isPractice ? 'practice-exam' : 'exam'
        }/time-remaining`,
        { timeRemaining: seconds },
      )
      .then(this.handleSetTimeRemaining)
      .catch(this.handleSetTimeRemainingError)
      .finally(() => {
        this.isSavingTimeRemaining = false
      })
  }
  @action handleSetTimeRemaining = (response) => {
    return Promise.resolve(response)
  }
  @action handleSetTimeRemainingError = (error) => {
    SentryReplay.captureException(error)
    return Promise.reject(error)
  }

  @action downloadCertificate() {
    this.isDownloadingCertificate = true
    return request
      .get(`/activities/${this.courseId}/exam-certificate`, {
        params: {
          d: Date.now(),
        },
      })
      .then(this.handleDownloadCertificate)
      .catch(this.handleDownloadCertificateError)
  }
  @action handleDownloadCertificate = (response) => {
    const { data: responseData } = response
    const { downloadUrl } = humps(responseData)
    this.isDownloadingCertificate = false
    window.location.assign(downloadUrl)
  }
  @action handleDownloadCertificateError = (error) => {
    SentryReplay.captureException(error)
    this.isDownloadingCertificate = false
    return Promise.reject(error)
  }
}
